<template>
  <v-parallax class="hero" :src="heroImage" alt="Jasmine Therapies gift vouchers">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2 animate-text">The perfect way to show you care</div>
        <div class="text-center text-h2 pa-2 animate-title">Give the gift of wellness</div>
      </div>
      <div class="text-center animate-button">
        <v-btn color="primary" href="#choose-voucher" size="large" class="book-now-button" elevation="4">Choose a Voucher</v-btn>
      </div>
    </div>
  </v-parallax>

  <section id="why-gift">
    <div class="full-width-header">Why a wellness voucher?</div>
    <div class="content pb-16">
      <p class="content-intro">
        A Jasmine Puozzo gift voucher is more than a present — it's an invitation to relax, recharge, and feel truly cared for.
        Perfect for birthdays, anniversaries, Mother's Day, or simply to say "you deserve this."
      </p>

      <div class="feature-grid">
        <div class="feature-item" v-for="item in whyGift" :key="item.title">
          <div class="feature-title">{{ item.title }}</div>
          <div class="feature-text">{{ item.description }}</div>
        </div>
      </div>

      <div class="text-center">
        <v-btn color="primary" href="#choose-voucher" size="large" class="book-now-button">View Vouchers</v-btn>
      </div>
    </div>
  </section>

  <section id="choose-voucher">
    <div class="full-width-header">Choose Your Voucher</div>
    <div class="content pb-16">
      <p class="content-intro">
        Select the perfect amount and let your loved one choose the treatment that suits them best — from relaxing 
        massages and reflexology to beauty treatments and nail care.
      </p>

      <div class="voucher-grid d-flex flex-wrap justify-center ga-12 px-4">
        <div class="voucher-card" v-for="voucher in vouchers" :key="voucher.value">
          <div class="voucher-amount text-center">{{ voucher.label }}</div>
          <div class="voucher-description text-center px-4">{{ voucher.description }}</div>
          <div class="voucher-action text-center pa-4">
            <v-btn color="primary" :href="voucher.url" target="_blank" class="book-now-button" prepend-icon="$mdi-gift-outline">Buy now!</v-btn>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section id="how-it-works">
    <div class="full-width-header">How it works</div>
    <div class="content pb-16">
      <div class="steps-grid">
        <div class="step-item" v-for="step in steps" :key="step.number">
          <div class="step-number">{{ step.number }}</div>
          <div class="step-title">{{ step.title }}</div>
          <div class="step-text">{{ step.description }}</div>
        </div>
      </div>
    </div>
  </section>

  <section id="occasions">
    <div class="full-width-header">Perfect for Every Occasion</div>
    <div class="content pb-16">
      <div class="occasions-grid">
        <div class="occasion-item" v-for="occasion in occasions" :key="occasion.label">
          <span class="occasion-icon">{{ occasion.icon }}</span>
          <span class="occasion-label">{{ occasion.label }}</span>
        </div>
      </div>
    </div>
  </section>

  <section id="cta">
    <div class="full-width-header">Make Someone's Day</div>
    <div class="content pb-16">
      <p class="content-intro">
        Whether it's a little treat or a grand gesture, a wellness voucher tells someone they deserve 
        to slow down, breathe, and be looked after. Give the gift of relaxation today.
      </p>
      <div class="text-center">
        <v-btn color="primary" href="#choose-voucher" size="large" class="book-now-button">Choose a Voucher</v-btn>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useDisplay } from 'vuetify';
import { BookingUrls } from '@/constants/booking';

export default defineComponent({
  name: 'GiftVouchersView',

  setup() {
    const { mobile } = useDisplay();
    return { mobile };
  },

  computed: {
    heroImage(): string {
      return this.mobile
        ? '/img/gift-vouchers-hero-vertical.webp'
        : '/img/gift-vouchers-hero.webp';
    }
  },

  data() {
    return {
      whyGift: [
        {
          title: 'A Thoughtful Gift',
          description: 'Show someone you truly care with a gift that prioritises their wellbeing. A wellness voucher says "take time for yourself."'
        },
        {
          title: 'Total Freedom of Choice',
          description: 'Your recipient can choose from our full range of massages, beauty treatments, reflexology, and nail services — whatever suits them best.'
        },
        {
          title: 'Suits Every Budget',
          description: 'With vouchers from €50 to €250, there\'s a perfect option whether you\'re treating a friend, a partner, or your whole family.'
        }
      ],

      vouchers: [
        {
          value: 50,
          label: '€50',
          description: 'Ideal for a relaxing head & neck massage or a beautiful manicure.',
          url: BookingUrls.GiftVouchers.Euro50
        },
        {
          value: 100,
          label: '€100',
          description: 'Perfect for a full-length massage session or a pampering beauty treatment.',
          url: BookingUrls.GiftVouchers.Euro100
        },
        {
          value: 150,
          label: '€150',
          description: 'A generous gift — enough for a luxurious treatment combination or multiple visits.',
          url: BookingUrls.GiftVouchers.Euro150
        },
        {
          value: 250,
          label: '€250',
          description: 'The ultimate wellness gift. A truly indulgent experience across any of our services.',
          url: BookingUrls.GiftVouchers.Euro250
        }
      ],

      steps: [
        {
          number: '1',
          title: 'Choose Your Amount',
          description: 'Pick the voucher value that suits your budget — €50, €100, €150, or €250.'
        },
        {
          number: '2',
          title: 'Complete Your Purchase',
          description: 'Buy securely online and receive the voucher details straight away.'
        },
        {
          number: '3',
          title: 'Gift & Enjoy',
          description: 'Share the voucher with your loved one. They book their favourite treatment whenever they\'re ready.'
        }
      ],

      occasions: [
        { icon: '🎂', label: 'Birthdays' },
        { icon: '💐', label: "Mother's Day" },
        { icon: '❤️', label: "Valentine's Day" },
        { icon: '🎄', label: 'Christmas' },
        { icon: '🎓', label: 'Graduations' },
        { icon: '💍', label: 'Weddings' },
        { icon: '🙏', label: 'Thank You Gifts' },
        { icon: '🤰', label: 'Mom-to-Be' },
        { icon: '🏡', label: 'New Home' },
      ]
    };
  }
});
</script>

<style scoped>
.animate-text, .animate-title, .animate-button {
  animation-duration: 1.5s;
  animation-fill-mode: both;
}

.animate-text {
  animation-name: fadeInDown;
  animation-delay: 0.3s;
}

.animate-title {
  animation-name: fadeInUp;
  animation-delay: 0.6s;
}

.animate-button {
  animation-name: fadeIn;
  animation-delay: 1s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translate3d(0, -30px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate3d(0, 30px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

/* Voucher cards */
.voucher-grid {
  max-width: 1200px;
  margin: 0 auto;
}

.voucher-card {
  width: 260px;
  border: 2px solid rgb(var(--v-theme-primary));
  border-radius: 20px;
  overflow: hidden;
  background-color: rgba(var(--v-theme-primary), 0.08);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 32px 16px 16px;
}

.voucher-card:hover {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transform: translateY(-4px);
}

.voucher-amount {
  font-size: 3rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 16px;
  letter-spacing: -1px;
}

.voucher-description {
  font-size: 16px;
  line-height: 1.5;
  opacity: 0.9;
  margin-bottom: 24px;
  flex-grow: 1;
}

.voucher-action .book-now-button {
  width: 100%;
}

/* Steps */
.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 28px auto 0;
  text-align: center;
}

.step-number {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: rgba(var(--v-theme-primary), 0.15);
  border: 2px solid rgb(var(--v-theme-primary));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 1.5rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.step-title {
  font-size: 22px;
  font-weight: 500;
  margin-bottom: 10px;
}

.step-text {
  font-size: 17px;
  line-height: 1.5;
  opacity: 0.9;
}

/* Occasions */
.occasions-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  max-width: 900px;
  margin: 28px auto 0;
}

.occasion-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  background: rgba(var(--v-theme-primary), 0.06);
  border-radius: 14px;
  border: 1px solid rgba(var(--v-theme-primary), 0.15);
  font-size: 17px;
  transition: background-color 0.2s ease;
}

.occasion-item:hover {
  background: rgba(var(--v-theme-primary), 0.12);
}

.occasion-icon {
  font-size: 24px;
}

@media (max-width: 960px) {
  .steps-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}

@media (max-width: 600px) {
  .voucher-card {
    width: 100%;
    max-width: 320px;
  }

  .voucher-amount {
    font-size: 2.5rem;
  }
}
</style>
